import React from "react";
import ReactDOM from "react-dom/client";
import { formatNumber } from "../../../../app/formatters/NumberFormatter";
import { ACTIVITY_TYPE } from "../../enums.js";

/** @typedef {import('../../models/RpaActivityModel.js').RpaActivityItem } RpaActivityItem */
/** @typedef {import('../../models/RpaServiceModel.js').RpaServiceItem } RpaServiceItem */
/** @typedef {import('../../models/RpaServiceRuleModel.js').RpaServiceRuleItem } RpaServiceRuleItem */

/**
 * @typedef {object} RpaServicesGroupedByType - Prestations regroupées par type renvoyées par l'API
 * @property {string} label - Libellé du type de prestation
 * @property {Array<RpaServiceItem>} rpaServices - Prestations du type
 */

/**
 * @typedef {object} RpaPriceCalculatorState
 * @property {Array<RpaActivityItem>} rpaActivities - Liste des activités
 * @property {Array<RpaServiceItem>} rpaServices - Liste des prestations actives (séances unitaires et cartes)
 * @property {?number} rpaActivityId - Identifiant de l'activité choisie
 * @property {number} nbAdults - Nombre de joueurs au tarif normal
 * @property {Object.<number, number>} nbPlayersByRule - Nombre de joueurs par règle de prix (tarif selon l'âge), l'id de la règle en clé
 * @property {number} nbHours - Nombre d'heures
 * @property {number} nbSessionsPerYear - Nombre de séances par an, pour estimer l'économie avec une carte
 * @property {0|1} priceType - Tarification choisie, 0 pour les heures pleines et 1 pour les heures creuses
 * @property {boolean} loading - Chargement des données en cours
 */

const TYPE_CARD = 0;
const TYPE_UNIC_SESSION = 2;
const PRICE_TYPE_LABELS = { 0: "Heures pleines", 1: "Heures creuses" };
const MAX_SESSIONS_PER_WEEK = 4;
const DAYS_PER_YEAR = 365;
const WEEKS_PER_YEAR = 52;
const MAX_SESSIONS_PER_YEAR = MAX_SESSIONS_PER_WEEK * WEEKS_PER_YEAR;

/**
 * @typedef {object} CardAnalysis - Analyse de l'intérêt d'une carte selon le nombre de séances par an
 * @property {RpaServiceItem} rpaService - Carte
 * @property {number} nbUsedSessions - Nombre de séances réellement utilisées avant la fin de validité de la carte
 * @property {number} nbCoveredSessions - Nombre de séances de l'année pouvant être payées avec des cartes
 * @property {number} saving - Économie annuelle par rapport aux séances unitaires
 */

class RpaPriceCalculator extends React.Component {
  /**
   * @param {*} props - proprité d'initialisation du composant React
   */
  constructor(props) {
    super(props);

    this.state = /** @type {RpaPriceCalculatorState} */ ({
      rpaActivities: [],
      rpaServices: [],
      rpaActivityId: null,
      nbAdults: 1,
      nbPlayersByRule: {},
      nbHours: 1,
      nbSessionsPerYear: 12,
      priceType: 0,
      loading: true
    });
  }

  async componentDidMount() {
    const [rpaActivities, rpaServicesGroupedByType] = await Promise.all([this.fetchRpaActivities(), this.fetchRpaServices()]);
    // NOTE Une carte hors ligne ne peut juste pas être achetée en ligne, on la garde ; les cartes à 0 € ne sont pas prises en compte
    const rpaServices = rpaServicesGroupedByType.flatMap(group => group.rpaServices).filter(rpaService => (rpaService.type === TYPE_CARD && parseFloat(rpaService.ttc_price) > 0) || (rpaService.type === TYPE_UNIC_SESSION && rpaService.state));

    this.setState({
      rpaActivities,
      rpaServices,
      rpaActivityId: rpaActivities.length ? rpaActivities[0].id : null,
      loading: false
    });
  }

  /**
   * Permet de récupérer la liste des activités
   * @returns {Promise<Array<RpaActivityItem>>} - Liste des activités
   */
  async fetchRpaActivities() {
    const response = await fetch("/api/rpa/search/activities", {
      headers: {
        Authorization: "gestiaweb"
      }
    });

    return await response.json();
  }

  /**
   * Permet de récupérer la liste des prestations regroupées par type
   * @returns {Promise<Array<RpaServicesGroupedByType>>} - Liste des prestations regroupées par type
   */
  async fetchRpaServices() {
    const response = await fetch("/api/rpa-service/search", {
      headers: {
        Authorization: "gestiaweb"
      }
    });

    return await response.json();
  }

  /**
   * Permet de récupérer les prestations d'un type associées à l'activité choisie
   * NOTE Comme pour les réservations, on privilégie les prestations associées directement à l'activité puis celles associées à une ressource de l'activité
   * @param {number} type - Type de prestation
   * @returns {Array<RpaServiceItem>} - Prestations disponibles pour l'activité
   */
  getRpaServicesOfActivity(type) {
    const rpaActivityId = this.state.rpaActivityId;
    const rpaServicesOfType = this.state.rpaServices.filter(rpaService => rpaService.type === type && this._isValidToday(rpaService));
    const rpaServicesForActivity = rpaServicesOfType.filter(rpaService => rpaService.rpa_activity_services.some(rpaActivityService => rpaActivityService.rpa_activity?.id === rpaActivityId));

    if (rpaServicesForActivity.length) {
      return rpaServicesForActivity;
    }

    return rpaServicesOfType.filter(rpaService => rpaService.rpa_activity_services.some(rpaActivityService => rpaActivityService.rpa_resource?.rpaActivityId === rpaActivityId));
  }

  /**
   * Permet de récupérer la séance unitaire correspondant à la tarification choisie
   * @param {Array<RpaServiceItem>} unicRpaServices - Séances unitaires de l'activité
   * @returns {?RpaServiceItem} - Séance unitaire trouvée
   */
  getUnicRpaService(unicRpaServices) {
    const filteredRpaServices = unicRpaServices.filter(rpaService => rpaService.price_type === this.state.priceType);
    // NOTE Si l'activité n'a pas de tarif pour la tarification choisie, on prend celle disponible
    const rpaServicesOfPriceType = filteredRpaServices.length ? filteredRpaServices : unicRpaServices;

    return rpaServicesOfPriceType.find(rpaService => rpaService.valid_for_reservation_from && rpaService.valid_for_reservation_to) || rpaServicesOfPriceType[0] || null;
  }

  /**
   * Permet de récupérer la carte la plus avantageuse pour le nombre de séances par an choisi (économie annuelle la plus haute)
   * NOTE Aucune carte n'est proposée si aucune ne permet de payer moins cher qu'en séances unitaires
   * @param {Array<RpaServiceItem>} cardRpaServices - Cartes de l'activité
   * @param {number} unicPrice - Prix d'une séance unitaire
   * @param {number} nbSessionsPerYear - Nombre de séances par an
   * @returns {?CardAnalysis} - Analyse de la carte la plus avantageuse
   */
  getBestCard(cardRpaServices, unicPrice, nbSessionsPerYear) {
    return cardRpaServices
      .filter(rpaService => rpaService.nb_session > 0)
      .map(rpaService => this._analyseCard(rpaService, unicPrice, nbSessionsPerYear))
      .filter(cardAnalysis => cardAnalysis.saving > 0)
      .reduce((bestCard, cardAnalysis) => (!bestCard || cardAnalysis.saving > bestCard.saving ? cardAnalysis : bestCard), /** @type {?CardAnalysis} */ (null));
  }

  /**
   * Choix d'une activité
   * @param {number} rpaActivityId - Identifiant de l'activité
   */
  onClickOnRpaActivity(rpaActivityId) {
    this.setState({ rpaActivityId, nbPlayersByRule: {} });
  }

  /**
   * Permet de savoir si le choix du nombre d'heures est proposé pour l'activité choisie
   * NOTE Une séance de quiz room ne se réserve pas à l'heure
   * @returns {boolean} - Si le nombre d'heures peut être choisi
   */
  canChooseHours() {
    const rpaActivity = this.state.rpaActivities.find(rpaActivity => rpaActivity.id === this.state.rpaActivityId);

    return rpaActivity?.activity_type !== ACTIVITY_TYPE.QUIZZROOM.value;
  }

  /**
   * Modification d'un compteur (adultes ou heures)
   * @param {'nbAdults'|'nbHours'} fieldName - Nom du compteur
   * @param {number} min - Valeur minimum du compteur
   * @param {number} step - Pas de modification (+1 ou -1)
   */
  onChangeCounter(fieldName, min, step) {
    this.setState(/** @type {any} */ (state => ({ [fieldName]: Math.max(state[fieldName] + step, min) })));
  }

  /**
   * Modification du nombre de joueurs pour une règle de prix
   * @param {number} rpaServiceRuleId - Identifiant de la règle de prix
   * @param {number} step - Pas de modification (+1 ou -1)
   */
  onChangeRuleCounter(rpaServiceRuleId, step) {
    this.setState(state => ({
      nbPlayersByRule: { ...state.nbPlayersByRule, [rpaServiceRuleId]: Math.max((state.nbPlayersByRule[rpaServiceRuleId] || 0) + step, 0) }
    }));
  }

  /**
   * Permet de savoir si une prestation est valable aujourd'hui en fonction de sa période de validité
   * @param {RpaServiceItem} rpaService - Prestation
   * @returns {boolean} - Si la prestation est valable
   * @private
   */
  _isValidToday(rpaService) {
    if (!rpaService.valid_for_reservation_from || !rpaService.valid_for_reservation_to) {
      return true;
    }

    const now = new Date();

    return new Date(rpaService.valid_for_reservation_from) <= now && now <= new Date(rpaService.valid_for_reservation_to);
  }

  /**
   * Permet de calculer le prix d'une séance avec une carte
   * @param {RpaServiceItem} cardRpaService - Carte
   * @returns {number} - Prix d'une séance
   * @private
   */
  _getPricePerSession(cardRpaService) {
    return parseFloat(cardRpaService.ttc_price) / cardRpaService.nb_session;
  }

  /**
   * Permet d'analyser l'intérêt d'une carte selon le rythme de la personne (séances par an ramenées en séances par jour)
   * - séances réellement utilisées avant la fin de validité : rythme par jour × durée de validité, limité au nombre de séances de la carte
   *   et au maximum de séances par semaine sur la durée de validité (sans durée de validité, on regarde sur un an)
   * - prix réel d'une séance : prix de la carte / séances réellement utilisées
   * - séances de l'année couvertes par des cartes : limitées par le maximum de séances par semaine, les autres restent en séances unitaires
   * @param {RpaServiceItem} cardRpaService - Carte
   * @param {number} unicPrice - Prix d'une séance unitaire
   * @param {number} nbSessionsPerYear - Nombre de séances par an
   * @returns {CardAnalysis} - Analyse de la carte
   * @private
   */
  _analyseCard(cardRpaService, unicPrice, nbSessionsPerYear) {
    const validityDays = cardRpaService.validity_duration || DAYS_PER_YEAR;
    const maxPerWeek = cardRpaService.nb_session_max_per_week;
    // NOTE Rythme par jour (séances par an / 365) × durée de validité, calculé dans cet ordre pour éviter les arrondis
    let nbUsedSessions = Math.min(cardRpaService.nb_session, Math.floor((nbSessionsPerYear * validityDays) / DAYS_PER_YEAR));

    if (maxPerWeek) {
      nbUsedSessions = Math.min(nbUsedSessions, Math.floor((maxPerWeek * validityDays) / 7));
    }

    const nbCoveredSessions = maxPerWeek ? Math.min(nbSessionsPerYear, maxPerWeek * WEEKS_PER_YEAR) : nbSessionsPerYear;
    const realPricePerSession = nbUsedSessions ? parseFloat(cardRpaService.ttc_price) / nbUsedSessions : Infinity;

    return {
      rpaService: cardRpaService,
      nbUsedSessions,
      nbCoveredSessions,
      saving: nbUsedSessions ? nbCoveredSessions * (unicPrice - realPricePerSession) : 0
    };
  }

  /**
   * Permet d'afficher le libellé d'une règle de prix selon l'âge
   * @param {RpaServiceRuleItem} rpaServiceRule - Règle de prix
   * @returns {string} - Libellé de la règle
   * @private
   */
  _getRuleLabel(rpaServiceRule) {
    if (rpaServiceRule.min_age !== null && rpaServiceRule.max_age !== null) {
      return `De ${rpaServiceRule.min_age} à ${rpaServiceRule.max_age} ans`;
    }

    return rpaServiceRule.max_age !== null ? `Moins de ${rpaServiceRule.max_age} ans` : `${rpaServiceRule.min_age} ans et +`;
  }

  /**
   * Permet d'afficher un compteur
   * @param {string} label - Libellé du compteur
   * @param {number} value - Valeur du compteur
   * @param {() => void} onDecrease - Diminution du compteur
   * @param {() => void} onIncrease - Augmentation du compteur
   * @returns {React.ReactNode} - Compteur
   */
  renderCounter(label, value, onDecrease, onIncrease) {
    return (
      <div className="rpapricecalculator__counter" key={label}>
        <span className="rpapricecalculator__label">{label}</span>
        <div className="buttongroup">
          <button type="button" className="buttongroup__button" onClick={onDecrease} aria-label={`Retirer - ${label}`}>
            <span className="material-icons">remove</span>
          </button>
          <span className="buttongroup__label">{value}</span>
          <button type="button" className="buttongroup__button" onClick={onIncrease} aria-label={`Ajouter - ${label}`}>
            <span className="material-icons">add</span>
          </button>
        </div>
      </div>
    );
  }

  /**
   * Permet d'afficher le résultat du calcul
   * @param {?RpaServiceItem} unicRpaService - Séance unitaire de l'activité
   * @returns {React.ReactNode} - Résultat du calcul
   */
  renderResult(unicRpaService) {
    if (!unicRpaService) {
      return <p className="rpapricecalculator__detail">Aucun tarif de séance unitaire n'est disponible pour cette activité.</p>;
    }

    const { nbAdults, nbSessionsPerYear, nbPlayersByRule } = this.state;
    const nbHours = this.canChooseHours() ? this.state.nbHours : 1;
    const unicPrice = parseFloat(unicRpaService.ttc_price);
    const rpaServiceRules = unicRpaService.rpa_service_rules || [];
    const priceDetails = nbAdults ? [`${nbAdults} × ${formatNumber(String(unicPrice), "€", 2)}`] : [];
    let totalPerHour = unicPrice * nbAdults;
    let nbPlayers = nbAdults;

    rpaServiceRules.forEach(rpaServiceRule => {
      const nb = nbPlayersByRule[rpaServiceRule.id] || 0;

      if (nb) {
        totalPerHour += parseFloat(rpaServiceRule.ttc_price) * nb;
        nbPlayers += nb;
        priceDetails.push(`${nb} × ${formatNumber(rpaServiceRule.ttc_price, "€", 2)}`);
      }
    });

    const cardRpaServices = this.getRpaServicesOfActivity(TYPE_CARD);
    const bestCard = this.getBestCard(cardRpaServices, unicPrice, nbSessionsPerYear);
    const cardRpaService = bestCard ? bestCard.rpaService : null;
    const cardPricePerSession = cardRpaService ? this._getPricePerSession(cardRpaService) : null;

    return (
      <>
        <span className="rpapricecalculator__label rpapricecalculator__label--main-color">Pour cette séance</span>
        <span className="rpapricecalculator__price">{formatNumber(String(totalPerHour * nbHours), "€", 2)}</span>
        <span className="rpapricecalculator__detail">
          {priceDetails.join(" + ") || "0 joueur"}
          {nbHours > 1 ? ` × ${nbHours} h` : ""}
        </span>
        {bestCard && cardRpaService && cardPricePerSession !== null ? (
          <div className="rpapricecalculator__card">
            <p className="rpapricecalculator__card-line">
              Avec la carte <span className="rpapricecalculator__highlight">{cardRpaService.name}</span> ({cardRpaService.nb_session} séances, {formatNumber(cardRpaService.ttc_price, "€", 2)}) : {formatNumber(String(cardPricePerSession), "€", 2)} la séance
            </p>
            <p className="rpapricecalculator__card-line">
              soit <strong>{formatNumber(String(cardPricePerSession * nbPlayers * nbHours), "€", 2)}</strong> pour cette séance et <span className="rpapricecalculator__highlight">{formatNumber(String(bestCard.saving), "€", 2)} d'économie</span> sur {nbSessionsPerYear} séances.
            </p>
            {bestCard.nbUsedSessions < cardRpaService.nb_session ? (
              <p className="rpapricecalculator__card-line">
                À ce rythme, tu utiliseras {bestCard.nbUsedSessions} séances sur {cardRpaService.nb_session} {cardRpaService.validity_duration ? `avant la fin de validité (${cardRpaService.validity_duration} jours)` : "sur un an"}.
              </p>
            ) : (
              ""
            )}
            {bestCard.nbCoveredSessions < nbSessionsPerYear ? (
              <p className="rpapricecalculator__card-line">
                Limitée à {cardRpaService.nb_session_max_per_week} séance(s) par semaine : {nbSessionsPerYear - bestCard.nbCoveredSessions} séances resteront au tarif unitaire.
              </p>
            ) : (
              ""
            )}
          </div>
        ) : cardRpaServices.length ? (
          <p className="rpapricecalculator__card">Pour {nbSessionsPerYear} séances par an, aucune carte n'est plus avantageuse que la séance unitaire.</p>
        ) : (
          ""
        )}
      </>
    );
  }

  render() {
    if (this.state.loading) {
      return "";
    }

    const unicRpaServices = this.getRpaServicesOfActivity(TYPE_UNIC_SESSION);
    const unicRpaService = this.getUnicRpaService(unicRpaServices);
    const rpaServiceRules = unicRpaService?.rpa_service_rules || [];
    const availablePriceTypes = [...new Set(unicRpaServices.map(rpaService => rpaService.price_type))].sort();
    const bookingUrl = `/rpa/index?rpaActivityId=${this.state.rpaActivityId}&returnurl=${encodeURIComponent(window.location.pathname + window.location.search)}`;

    return (
      <div className="rpapricecalculator">
        <div className="rpapricecalculator__form">
          <div className="rpapricecalculator__block">
            <span className="rpapricecalculator__label">Je joue à…</span>
            <div className="rpapricecalculator__choices">
              {this.state.rpaActivities.map(rpaActivity => (
                <button type="button" key={rpaActivity.id} className={`btn btn--small rpapricecalculator__choice ${rpaActivity.id === this.state.rpaActivityId ? "btn--secondary-color" : "btn--outline-main-color"}`} onClick={this.onClickOnRpaActivity.bind(this, rpaActivity.id)}>
                  {rpaActivity.name}
                </button>
              ))}
            </div>
          </div>
          {availablePriceTypes.length > 1 ? (
            <div className="rpapricecalculator__block">
              <span className="rpapricecalculator__label">Tarification</span>
              <div className="rpapricecalculator__choices">
                {availablePriceTypes.map(priceType => (
                  <button type="button" key={priceType} className={`btn btn--small rpapricecalculator__choice ${priceType === this.state.priceType ? "btn--secondary-color" : "btn--outline-main-color"}`} onClick={() => this.setState({ priceType })}>
                    {PRICE_TYPE_LABELS[priceType]}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            ""
          )}
          <div className="rpapricecalculator__counters">
            {this.renderCounter(
              rpaServiceRules.length ? "Adultes" : "Joueurs",
              this.state.nbAdults,
              () => this.onChangeCounter("nbAdults", 0, -1),
              () => this.onChangeCounter("nbAdults", 0, 1)
            )}
            {rpaServiceRules.map(rpaServiceRule =>
              this.renderCounter(
                this._getRuleLabel(rpaServiceRule),
                this.state.nbPlayersByRule[rpaServiceRule.id] || 0,
                () => this.onChangeRuleCounter(rpaServiceRule.id, -1),
                () => this.onChangeRuleCounter(rpaServiceRule.id, 1)
              )
            )}
            {this.canChooseHours()
              ? this.renderCounter(
                  "Heures",
                  this.state.nbHours,
                  () => this.onChangeCounter("nbHours", 1, -1),
                  () => this.onChangeCounter("nbHours", 1, 1)
                )
              : ""}
          </div>
          <div className="rpapricecalculator__block">
            <label htmlFor="rpapricecalculator-sessions" className="rpapricecalculator__label">
              Et tu viens souvent ? <strong>{this.state.nbSessionsPerYear} séances par an</strong>
            </label>
            <input id="rpapricecalculator-sessions" className="rpapricecalculator__range" type="range" min="1" max={MAX_SESSIONS_PER_YEAR} step="1" value={this.state.nbSessionsPerYear} onChange={event => this.setState({ nbSessionsPerYear: parseInt(event.target.value, 10) })} />
          </div>
        </div>
        <div className="rpapricecalculator__summary background--secondary-color">
          {this.renderResult(unicRpaService)}
          <a className="btn btn--main-color btn--full rpapricecalculator__booking" href={bookingUrl}>
            Réserver
          </a>
        </div>
      </div>
    );
  }
}

var rpaPriceCalculatorNode = document.getElementById("rpa-price-calculator");
if (rpaPriceCalculatorNode) {
  var root = ReactDOM.createRoot(rpaPriceCalculatorNode);
  root.render(<RpaPriceCalculator />);
}
